import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import {
  compileRestrictedPolicyCriteria
} from '../utils/restrictedPolicyCriteriaCompiler';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import {
  getAvailableEnvironments,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const createSimulationForm = () => ({
  instanceId: '',
  simulationId: '',
  simulationName: '',
  routingPolicyId: '',
  restrictionId: '',
  contactAttributes: {},
  active: true
});

const RoutingSimulationPage = () => {
  const [simulations, setSimulations] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedSimulation, setSelectedSimulation] = useState(createSimulationForm());
  const [buildForm, setBuildForm] = useState(createSimulationForm());
  const [buildOptions, setBuildOptions] = useState({
    policies: [],
    restrictions: [],
    attributes: []
  });
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingSimulations, setLoadingSimulations] = useState(false);
  const [loadingBuildOptions, setLoadingBuildOptions] = useState(false);
  const [loadingRun, setLoadingRun] = useState(false);
  const [loadingCompile, setLoadingCompile] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showSelectedSection, setShowSelectedSection] = useState(true);
  const [showBuildSection, setShowBuildSection] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showResultJson, setShowResultJson] = useState(false);
  const [showBuildJson, setShowBuildJson] = useState(false);
  const [compiledCriteria, setCompiledCriteria] = useState(null);
  const [showCompiledCriteria, setShowCompiledCriteria] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());

  const availableEnvironments = getAvailableEnvironments();
  const endpoints = getApiEndpoints(selectedEnvironment);

  const getUserRole = () => (
    JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
  );

  // Handle environment change
  const handleEnvironmentChange = (e) => {
        const newEnvironment = e.target.value;
        setSelectedEnvironment(newEnvironment);
        saveSelectedEnvironment(newEnvironment);
        setResult(null);
        setError(null);
        setCompiledCriteria(null);
        setShowCompiledCriteria(false);
   }

  const fetchRoutingSimulations = async (instanceId) => {
    setLoadingSimulations(true);
    setSimulations([]);

    try {
      const response = await apiService.post(endpoints.simulations, { instanceId });
      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to load routing simulations');
      }

      setSimulations(response.data.simulations || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load routing simulations');
    } finally {
      setLoadingSimulations(false);
    }
  };

  const loadBuildOptions = async (instanceId) => {
    setLoadingBuildOptions(true);
    setBuildOptions({ policies: [], restrictions: [], attributes: [] });

    try {
      const [policiesResponse, restrictionsResponse, attributesResponse] = await Promise.all([
        apiService.post(endpoints.routingPolicies, {
          instanceId: instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.routingRestrictions, {
          instanceId: instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.attributesView, {
          instance_id: instanceId,
          user_role: getUserRole()
        })
      ]);

      if ([policiesResponse, restrictionsResponse, attributesResponse].some(
        (response) => response.data.status !== 'ok'
      )) {
        throw new Error('Failed to load simulation options');
      }

      setBuildOptions({
        policies: policiesResponse.data.result?.routingPolicies || [],
        restrictions: restrictionsResponse.data.result?.routingRestrictions || [],
        attributes: attributesResponse.data.result?.attributes || []
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load simulation options');
    } finally {
      setLoadingBuildOptions(false);
    }
  };

  const handleSelectedInstanceChange = (e) => {
    const instanceId = e.target.value;

    setSelectedSimulation({ ...createSimulationForm(), instanceId });
    setError(null);
    setSimulationResult(null);
    setShowResultJson(false);
    setCompiledCriteria(null);
    setShowCompiledCriteria(false);

    if (instanceId) {
      fetchRoutingSimulations(instanceId);
    } else {
      setSimulations([]);
    }
  };

  const handleSimulationChange = async (e) => {
    const simulation = simulations.find((item) => item.simulationId === e.target.value);
    const instanceId = selectedSimulation.instanceId;

    setSelectedSimulation({
      instanceId,
      simulationId: simulation?.simulationId || '',
      simulationName: simulation?.simulationName || '',
      routingPolicyId: simulation?.routingPolicyId || '',
      routingPolicyName: simulation?.routingPolicyName || '',
      restrictionId: simulation?.restrictionId || '',
      restrictionName: simulation?.restrictionName || '',
      contactAttributes: simulation?.contactAttributes || {},
      active: simulation?.active ?? true
    });
    setError(null);
    setSimulationResult(null);
    setShowResultJson(false);
    setCompiledCriteria(null);
    setShowCompiledCriteria(false);

    if (!simulation?.simulationId) return;

    setLoadingSimulations(true);
    try {
      const [simulationResponse, policiesResponse, restrictionsResponse] = await Promise.all([
        apiService.post(endpoints.routingSimulation, {
          instanceId,
          simulationId: simulation.simulationId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.routingPolicies, {
          instanceId,
          user_role: getUserRole()
        }),
        apiService.post(endpoints.routingRestrictions, {
          instanceId,
          user_role: getUserRole()
        })
      ]);
      if ([simulationResponse, policiesResponse, restrictionsResponse]
        .some((response) => response.data.status !== 'ok')) {
        throw new Error('Failed to load routing simulation references');
      }

      const simulationDetails = simulationResponse.data.result?.simulation || {};
      const policy = (policiesResponse.data.result?.routingPolicies || [])
        .find((item) => item.policyId === (simulationDetails.routingPolicyId || simulation.routingPolicyId));
      const restriction = (restrictionsResponse.data.result?.routingRestrictions || [])
        .find((item) => item.restrictionId === (simulationDetails.restrictionId || simulation.restrictionId));

      setSelectedSimulation((current) => ({
        ...current,
        ...simulationDetails,
        instanceId,
        simulationId: simulation.simulationId,
        routingPolicyName: policy?.policyName || simulationDetails.routingPolicyName || current.routingPolicyName,
        restrictionName: restriction?.restrictionName || simulationDetails.restrictionName || current.restrictionName
      }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load routing simulation');
    } finally {
      setLoadingSimulations(false);
    }
  };

  const handleRunSimulation = async () => {
    if (!selectedSimulation.simulationId) return;

    setLoadingRun(true);
    setError(null);
    setSimulationResult(null);
    setShowResultJson(false);

    try {
      const request = {
        instanceId: selectedSimulation.instanceId,
        user_role: getUserRole()
      };
      const attributesRequest = {
        instance_id: selectedSimulation.instanceId,
        user_role: getUserRole()
      };
      const [policiesResponse, restrictionsResponse, groupsResponse, attributesResponse] = await Promise.all([
        apiService.post(endpoints.routingPolicies, request),
        apiService.post(endpoints.routingRestrictions, request),
        apiService.post(endpoints.routingGroups, request),
        apiService.post(endpoints.attributesView, attributesRequest)
      ]);

      if ([policiesResponse, restrictionsResponse, groupsResponse, attributesResponse]
        .some((response) => response.data.status !== 'ok')) {
        throw new Error('Failed to load routing simulation inputs');
      }

      const policy = (policiesResponse.data.result?.routingPolicies || [])
        .find((item) => item.policyId === selectedSimulation.routingPolicyId);
      const restriction = (restrictionsResponse.data.result?.routingRestrictions || [])
        .find((item) => item.restrictionId === selectedSimulation.restrictionId);
      if (!policy) throw new Error(`Routing policy '${selectedSimulation.routingPolicyId}' was not found.`);
      if (!restriction) throw new Error(`Routing restriction '${selectedSimulation.restrictionId}' was not found.`);

      const normalizedPolicy = {
        ...policy,
        routingSteps: (policy.routingSteps || []).map((step, index) => ({
          ...step,
          step: Number(step.step ?? step.priority ?? index + 1)
        }))
      };
      const routingGroups = groupsResponse.data.result?.routingGroups || [];
      const attributes = attributesResponse.data.result?.attributes || [];
      const compiled = compileRestrictedPolicyCriteria(
        normalizedPolicy,
        restriction,
        selectedSimulation.contactAttributes,
        routingGroups,
        attributes
      );
      const appliedPolicy = compiled.appliedPolicy;
      const transformedStepIds = new Set(appliedPolicy.routingSteps.map((step) => step.routingGroupId));
      const matched = compiled.restrictionMatched;
      const formatTimeout = (timeoutSeconds) => (
        timeoutSeconds == null ? 'Unlimited' : `${timeoutSeconds} Seconds`
      );
      const action = restriction.actions?.[0] || {};
      const effectiveRoutingOutcome = {
        routingGroupIds: normalizedPolicy.routingSteps.map((step) => ({
          step: step.step,
          group: step.routingGroupId,
          timeout: formatTimeout(
            matched && action.type === 'CHANGE_TIMEOUT' && step.timeoutSeconds != null
              ? action.timeoutSeconds
              : step.timeoutSeconds
          ),
          result: !matched || transformedStepIds.has(step.routingGroupId) ? 'ACTIVE' : 'EXCLUDED'
        }))
      };

      setSelectedSimulation((current) => ({
        ...current,
        routingPolicyName: policy.policyName || current.routingPolicyName,
        restrictionName: restriction.restrictionName || current.restrictionName
      }));
      setSimulationResult({
        simulationId: selectedSimulation.simulationId,
        result: 'success',
        contactAttributes: selectedSimulation.contactAttributes,
        baseRoutingPolicy: normalizedPolicy,
        restrictionEvaluation: {
          restrictionId: restriction.restrictionId,
          restrictionName: restriction.restrictionName,
          contactAttributes: selectedSimulation.contactAttributes,
          matched,
          action
        },
        effectiveRoutingOutcome,
        finalRoutingPath: appliedPolicy.routingSteps.map((step) => ({
          step: step.step,
          group: step.routingGroupId,
          timeout: formatTimeout(step.timeoutSeconds),
          result: 'ACTIVE'
        })),
        compiledCriteria: compiled.RoutingCriteria
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to run routing simulation');
    } finally {
      setLoadingRun(false);
    }
  };

  const handleApplyRestrictionAndCompile = async () => {
    if (!selectedSimulation.simulationId) return;

    setLoadingCompile(true);
    setError(null);
    setCompiledCriteria(null);
    setShowCompiledCriteria(false);

    try {
      const request = {
        instanceId: selectedSimulation.instanceId,
        user_role: getUserRole()
      };
      
      const attrRequest = {
        instance_id: selectedSimulation.instanceId,
        user_role: getUserRole()
      };

      const [policiesResponse, restrictionsResponse, groupsResponse, attributesResponse] = await Promise.all([
        apiService.post(endpoints.routingPolicies, request),
        apiService.post(endpoints.routingRestrictions, request),
        apiService.post(endpoints.routingGroups, request),
        apiService.post(endpoints.attributesView, attrRequest)
      ]);

      if ([policiesResponse, restrictionsResponse, groupsResponse, attributesResponse]
        .some((response) => response.data.status !== 'ok')) {
        throw new Error('Failed to load routing criteria inputs');
      }

      const policy = (policiesResponse.data.result?.routingPolicies || [])
        .find((item) => item.policyId === selectedSimulation.routingPolicyId);
      const restriction = (restrictionsResponse.data.result?.routingRestrictions || [])
        .find((item) => item.restrictionId === selectedSimulation.restrictionId);
      if (!policy) throw new Error(`Routing policy '${selectedSimulation.routingPolicyId}' was not found.`);
      if (!restriction) throw new Error(`Routing restriction '${selectedSimulation.restrictionId}' was not found.`);

      const normalizedPolicy = {
        ...policy,
        routingSteps: (policy.routingSteps || []).map((step, index) => ({
          ...step,
          step: Number(step.step ?? step.priority ?? index + 1)
        }))
      };
      const compiled = compileRestrictedPolicyCriteria(
        normalizedPolicy,
        restriction,
        selectedSimulation.contactAttributes,
        groupsResponse.data.result?.routingGroups || [],
        attributesResponse.data.result?.attributes || []
      );

      setCompiledCriteria(compiled);
      setShowCompiledCriteria(true);
    } catch (err) {
      const messages = Array.isArray(err.errors)
        ? err.errors.map(({ message }) => message).join(' ')
        : err.message;
      setError(messages || 'Failed to apply restriction and compile routing criteria.');
    } finally {
      setLoadingCompile(false);
    }
  };

  const handleBuildInstanceChange = (e) => {
    const instanceId = e.target.value;

    setBuildForm({ ...createSimulationForm(), instanceId });
    setError(null);

    if (instanceId) {
      loadBuildOptions(instanceId);
    } else {
      setBuildOptions({ policies: [], restrictions: [], attributes: [] });
    }
  };

  const updateBuildForm = (field, value) => {
    setBuildForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateContactAttributes = (e) => {
    const contactAttributes = Array.from(e.target.selectedOptions).reduce(
      (values, option) => ({
        ...values,
        [option.dataset.attributeId]: option.dataset.attributeValue
      }),
      {}
    );

    updateBuildForm('contactAttributes', contactAttributes);
  };

  const getBuildSimulationPayload = () => ({
    ...buildForm,
    user_role: getUserRole()
  });

  const handleCreateSimulation = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiService.post(endpoints.routingSimulationRegistry, {
        ...buildForm,
        user_role: getUserRole()
      });

      if (response.data.status !== 'ok') {
        throw new Error(response.data.message || 'Failed to create routing simulation');
      }

      await fetchRoutingSimulations(buildForm.instanceId);
      setBuildForm((prev) => ({
        ...createSimulationForm(),
        instanceId: prev.instanceId
      }));
      setResult({ message: 'Routing simulation created successfully.' });
      setShowBuildJson(false);
      setShowSelectedSection(true);
      setShowBuildSection(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create routing simulation');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);

      try {
        const response = await apiService.get(endpoints.instanceDropdown);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load instances');
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [endpoints.instanceDropdown]);

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Routing Simulation</h1>
          <p className="page-subtitle">Select, run, or build routing simulations</p>
        </div>
        <Link to="/routing-admin" className="btn btn-secondary">
          ← Back to Routing Administration
        </Link>
      </div>

       {/* Environment Selector */}
       <div className="environment-selector" style={{
            backgroundColor: '#f9f9f9',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
            }}>
            <label htmlFor="environment" style={{ 
                fontWeight: 'bold', 
                fontSize: '16px',
                minWidth: '120px'
            }}>
                Environment:
            </label>
            <select
                id="environment"
                value={selectedEnvironment}
                onChange={handleEnvironmentChange}
                style={{
                padding: '10px 16px',
                fontSize: '15px',
                borderRadius: '6px',
                border: '2px solid #ccc',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontWeight: '500',
                minWidth: '200px'
                }}
            >
                {availableEnvironments.map(env => (
                <option key={env} value={env}>
                    {ENVIRONMENT_LABELS[env]} ({env})
                </option>
                ))}
            </select>
            <span style={{
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: getEnvironmentColor(selectedEnvironment)
            }}>
                {selectedEnvironment}
            </span>
        </div>

      <div className="form-container">
        <div className="setup-form">
          <div className="form-section">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowSelectedSection((visible) => !visible)}
            >
              {showSelectedSection ? 'Hide' : 'Show'} Run Routing Simulation
            </button>

            {showSelectedSection && (
              <>
                <h3>Run Routing Simulation</h3>
                  <div className="form-group">
                    <label htmlFor="selectedInstanceId">Instance *</label>
                    <select
                      id="selectedInstanceId"
                      value={selectedSimulation.instanceId}
                      onChange={handleSelectedInstanceChange}
                      disabled={loadingInstances}
                    >
                      <option value="">
                        {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                      </option>
                      {instances.map((instance) => (
                        <option key={instance.id} value={instance.id}>
                          {instance.name} ({instance.status})
                        </option>
                      ))}
                    </select>
                    <small>Select an Amazon Connect instance</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="simulationId">Simulation ID *</label>
                    <select
                      id="simulationId"
                      value={selectedSimulation.simulationId}
                      onChange={handleSimulationChange}
                      disabled={!selectedSimulation.instanceId || loadingSimulations}
                    >
                      <option value="">
                        {loadingSimulations ? 'Loading simulations...' : 'Select a simulation...'}
                      </option>
                      {simulations.map((simulation) => (
                        <option key={simulation.simulationId} value={simulation.simulationId}>
                          {simulation.simulationName} ({simulation.simulationId})
                        </option>
                      ))}
                    </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="routingPolicy">Routing Policy</label>
                    <input
                      id="routingPolicy"
                      value={selectedSimulation.routingPolicyName && selectedSimulation.routingPolicyId
                        ? `${selectedSimulation.routingPolicyName} (${selectedSimulation.routingPolicyId})`
                        : ''}
                      readOnly
                      placeholder="Select a simulation"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="restriction">Routing Restriction</label>
                    <input
                      id="restriction"
                      value={selectedSimulation.restrictionName && selectedSimulation.restrictionId
                        ? `${selectedSimulation.restrictionName} (${selectedSimulation.restrictionId})`
                        : ''}
                      readOnly
                      placeholder="Select a simulation"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="selectedAttributes">Contact Attributes</label>
                  <textarea
                    id="selectedAttributes"
                    value={Object.entries(selectedSimulation.contactAttributes)
                      .map(([attribute, value]) => `${attribute}: ${value}`)
                      .join('\n')}
                    readOnly
                    rows="4"
                    placeholder="Select a simulation"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRunSimulation}
                    disabled={!selectedSimulation.simulationId || loadingRun}
                  >
                    {loadingRun ? 'Running...' : 'Run Simulation'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleApplyRestrictionAndCompile}
                    disabled={!selectedSimulation.simulationId || loadingCompile}
                  >
                    {loadingCompile ? 'Compiling...' : 'Compile & Apply Restriction'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleCreateSimulation} className="setup-form">
          <div className="form-section">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowBuildSection((visible) => !visible)}
            >
              {showBuildSection ? 'Hide' : 'Show'} Build Routing Simulation
            </button>

            {showBuildSection && (
              <>
                <h3>Build Routing Simulation</h3>
                <div className="form-group">
                    <label htmlFor="buildInstanceId">Instance *</label>
                    <select
                      id="buildInstanceId"
                      value={buildForm.instanceId}
                      onChange={handleBuildInstanceChange}
                      required
                      disabled={loadingInstances}
                    >
                      <option value="">
                        {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                      </option>
                      {instances.map((instance) => (
                        <option key={instance.id} value={instance.id}>
                          {instance.name} ({instance.status})
                        </option>
                      ))}
                    </select>
                    <small>Select an Amazon Connect instance</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="newSimulationId">Simulation ID *</label>
                    <input
                      id="newSimulationId"
                      value={buildForm.simulationId}
                      onChange={(e) => updateBuildForm('simulationId', e.target.value)}
                      required
                      maxLength={256}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="simulationName">Simulation Name *</label>
                    <input
                      id="simulationName"
                      value={buildForm.simulationName}
                      onChange={(e) => updateBuildForm('simulationName', e.target.value)}
                      required
                      maxLength={256}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="policyId">Routing Policy *</label>
                    <select
                      id="policyId"
                      value={buildForm.routingPolicyId}
                      onChange={(e) => updateBuildForm('routingPolicyId', e.target.value)}
                      required
                      disabled={!buildForm.instanceId || loadingBuildOptions}
                    >
                      <option value="">Selected Routing Policy</option>
                      {buildOptions.policies.map((policy) => (
                        <option key={policy.policyId} value={policy.policyId}>
                          {policy.policyName} ({policy.policyId})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="restrictionId">Routing Restriction *</label>
                    <select
                      id="restrictionId"
                      value={buildForm.restrictionId}
                      onChange={(e) => updateBuildForm('restrictionId', e.target.value)}
                      required
                      disabled={!buildForm.instanceId || loadingBuildOptions}
                    >
                      <option value="">Selected Routing Restriction</option>
                      {buildOptions.restrictions.map((restriction) => (
                        <option key={restriction.restrictionId} value={restriction.restrictionId}>
                          {restriction.restrictionName} ({restriction.restrictionId})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="buildAttributes">Contact Attributes *</label>
                  <select
                    id="buildAttributes"
                    multiple
                    rows={8}
                    value={Object.entries(buildForm.contactAttributes)
                      .map(([attributeId, value]) => `${attributeId}:${value}`)}
                    onChange={updateContactAttributes}
                    required
                    disabled={!buildForm.instanceId || loadingBuildOptions}
                  >
                    {buildOptions.attributes
                      .filter((attribute) => attribute.active !== false)
                      .flatMap((attribute) => (
                        (attribute.allowedValues || []).map((allowedValue) => (
                          <option
                            key={`${attribute.attributeId}-${allowedValue.value}`}
                            value={`${attribute.attributeId}:${allowedValue.value}`}
                            data-attribute-id={attribute.attributeId}
                            data-attribute-value={allowedValue.value}
                          >
                            {attribute.attributeId}: {allowedValue.value} ({allowedValue.displayName})
                          </option>
                        ))
                      ))}
                  </select>
                </div>

                <div className="form-group attribute-active-control">
                  <label htmlFor="simulationActive">
                    <input
                      id="simulationActive"
                      type="checkbox"
                      checked={buildForm.active}
                      onChange={(e) => updateBuildForm('active', e.target.checked)}
                    />
                    Active
                  </label>
                </div>
              </>
            )}
          </div>

          {showBuildSection && (
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowBuildJson(true)}
              >
                View JSON
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating || loadingBuildOptions}
              >
                {creating ? 'Creating...' : 'Create Routing Simulation'}
              </button>
            </div>
          )}
        </form>
      </div>

      {showBuildJson && (
        <div
          className="json-preview-overlay"
          role="presentation"
          onClick={() => setShowBuildJson(false)}
        >
          <div
            className="json-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="build-simulation-json-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="json-preview-header">
              <h3 id="build-simulation-json-title">Routing Simulation JSON</h3>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowBuildJson(false)}
              >
                Close
              </button>
            </div>
            <pre className="json-preview-content">
              {JSON.stringify(getBuildSimulationPayload(), null, 2)}
            </pre>
          </div>
        </div>
      )}

      {simulationResult && (
        <div className="table-format-list">
          <div className="table-format-list-header">
            <h3>
              Simulation Result: {selectedSimulation.simulationName || simulationResult.simulationId}
              {' '}({simulationResult.simulationId} - {simulationResult.result})
            </h3>
          </div>

          <div className="form-section">
            <h3>Base Routing Policy</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="baseRoutingPolicyId">Policy ID</label>
                <input
                  id="baseRoutingPolicyId"
                  value={simulationResult.baseRoutingPolicy?.policyId || simulationResult.baseRoutingPolicy?.routingPolicyId || '-'}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="baseRoutingPolicyName">Policy Name</label>
                <input
                  id="baseRoutingPolicyName"
                  value={simulationResult.baseRoutingPolicy?.policyName || selectedSimulation.routingPolicyName || '-'}
                  readOnly
                />
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Routing Group</th>
                  <th>Timeout</th>
                </tr>
              </thead>
              <tbody>
                {(simulationResult.baseRoutingPolicy?.routingSteps || []).map((step, index) => (
                  <tr key={`${step.routingGroupId}-${index}`}>
                    <td>{step.step}</td>
                    <td>{step.routingGroupId}</td>
                    <td>{step.timeoutSeconds == null ? 'Unlimited' : `${step.timeoutSeconds} Seconds`}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3>Selected Restriction</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="resultRestrictionId">Restriction ID</label>
                <input
                  id="resultRestrictionId"
                  value={simulationResult.restrictionEvaluation?.restrictionId || 'None'}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="resultRestrictionName">Restriction Name</label>
                <input
                  id="resultRestrictionName"
                  value={selectedSimulation.restrictionName || 'None'}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="resultRestrictionAction">Action</label>
                <input
                  id="resultRestrictionAction"
                  value={simulationResult.restrictionEvaluation?.action?.type || 'None'}
                  readOnly
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="resultRestrictionGroups">Affected Routing Groups</label>
              <textarea
                id="resultRestrictionGroups"
                value={(simulationResult.restrictionEvaluation?.action?.routingGroupIds || []).join('\n')}
                readOnly
                rows="3"
              />
            </div>

            <h3>Effective Routing Outcome</h3>
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Routing Group</th>
                  <th>Timeout</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {(simulationResult.effectiveRoutingOutcome?.routingGroupIds || []).map((step, index) => (
                  <tr key={`${step.group}-${index}`}>
                    <td>{step.step || index + 1}</td>
                    <td>{step.group}</td>
                    <td>{step.timeout}</td>
                    <td>{step.result || 'Active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3>Final Routing Path</h3>
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Routing Group</th>
                  <th>Timeout</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {(simulationResult.finalRoutingPath || []).map((step, index) => (
                  <tr key={`${step.group}-${index}`}>
                    <td>{step.step || index + 1}</td>
                    <td>{step.group}</td>
                    <td>{step.timeout}</td>
                    <td>{step.result || 'Active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowResultJson(true)}
              >
                View Result JSON
              </button>
            </div>
          </div>
        </div>
      )}

      {showResultJson && simulationResult && (
        <div
          className="json-preview-overlay"
          role="presentation"
          onClick={() => setShowResultJson(false)}
        >
          <div
            className="json-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="simulation-result-json-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="json-preview-header">
              <h3 id="simulation-result-json-title">Routing Simulation Result JSON</h3>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowResultJson(false)}
              >
                Close
              </button>
            </div>
            <pre className="json-preview-content">
              {JSON.stringify(simulationResult, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {showCompiledCriteria && compiledCriteria && (
        <div
          className="json-preview-overlay"
          role="presentation"
          onClick={() => setShowCompiledCriteria(false)}
        >
          <div
            className="json-preview-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="compiled-routing-criteria-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="json-preview-header">
              <h3 id="compiled-routing-criteria-title">Restricted AWS Connect Routing Criteria</h3>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCompiledCriteria(false)}
              >
                Close
              </button>
            </div>
            <pre className="json-preview-content">
              {JSON.stringify(compiledCriteria, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}
      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          <p>{result.message}</p>
        </div>
      )}
    </div>
  );
};

export default RoutingSimulationPage;